import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  useTheme,
  alpha,
  CircularProgress,
  Chip,
  Fade,
  Slide,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  School,
  Person,
  Lock,
  Security,
  CheckCircle,
  AccountCircle,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { LoginCredentials } from '../../types';

export function Login() {
  const theme = useTheme();
  const { login, loading, error, clearError } = useAuth();
  const { showToast } = useApp();
  
  const [credentials, setCredentials] = useState<LoginCredentials>({
    username: '',
    password: '',
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [formFocused, setFormFocused] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Load remembered username
    const rememberedUsername = localStorage.getItem('rememberedUsername');
    if (rememberedUsername) {
      setCredentials(prev => ({
        ...prev,
        username: rememberedUsername,
        rememberMe: true,
      }));
    }
  }, []);

  useEffect(() => {
    if (error) {
      showToast(error, 'error');
      clearError();
    }
  }, [error, showToast, clearError]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!credentials.username.trim()) {
      errors.username = 'Username is required';
    } else if (credentials.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }

    if (!credentials.password) {
      errors.password = 'Password is required';
    } else if (credentials.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await login(credentials);
      showToast('Welcome back! 🎉', 'success');
    } catch (err) {
      // Error is handled by useEffect above
    }
  };

  const handleInputChange = (field: keyof LoginCredentials) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'rememberMe' ? e.target.checked : e.target.value;
    setCredentials(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const securityFeatures = [
    { icon: <Security style={{ fontSize: 16, color: 'white' }} />, text: 'End-to-end encryption' },
    { icon: <CheckCircle style={{ fontSize: 16, color: 'white' }} />, text: 'Secure authentication' },
    { icon: <AccountCircle style={{ fontSize: 16, color: 'white' }} />, text: 'Role-based access' },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.mode === 'light'
          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          : 'linear-gradient(135deg, #232526 0%, #414345 100%)',
        p: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Enhanced Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 70%)`,
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-20px) rotate(180deg)' },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '10%',
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.common.white, 0.08)} 0%, transparent 70%)`,
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '5%',
          width: 100,
          height: 100,
          borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
          background: `linear-gradient(45deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, transparent 70%)`,
          animation: 'morphing 10s ease-in-out infinite',
          '@keyframes morphing': {
            '0%, 100%': { 
              borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
              transform: 'translate(0, 0) scale(1)',
            },
            '25%': { 
              borderRadius: '58% 42% 75% 25% / 76% 46% 54% 24%',
              transform: 'translate(10px, -10px) scale(1.1)',
            },
            '50%': { 
              borderRadius: '50% 50% 33% 67% / 55% 27% 73% 45%',
              transform: 'translate(-5px, 10px) scale(0.9)',
            },
            '75%': { 
              borderRadius: '33% 67% 58% 42% / 63% 68% 32% 37%',
              transform: 'translate(15px, 5px) scale(1.05)',
            },
          },
        }}
      />

      <Fade in={mounted} timeout={1000}>
        <Card
          sx={{
            maxWidth: 500,
            width: '100%',
            boxShadow: formFocused 
              ? '0 40px 80px -12px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.1)'
              : '0 32px 64px -12px rgba(0, 0, 0, 0.25)',
            borderRadius: 4,
            overflow: 'hidden',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: mounted ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
            '&:hover': {
              boxShadow: '0 40px 80px -12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.15)',
              transform: 'translateY(-5px) scale(1.02)',
            },
          }}
        >
          {/* Enhanced Header */}
          <Box
            sx={{
              background: theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'linear-gradient(135deg, #8360c3 0%, #2ebf91 100%)',
              color: 'white',
              p: 5,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Enhanced Background Pattern */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  radial-gradient(circle at 20% 80%, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 50%),
                  radial-gradient(circle at 80% 20%, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 50%),
                  radial-gradient(circle at 40% 40%, ${alpha(theme.palette.common.white, 0.05)} 0%, transparent 70%)
                `,
                pointerEvents: 'none',
              }}
            />
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Slide direction="down" in={mounted} timeout={800}>
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.common.white, 0.15),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    backdropFilter: 'blur(10px)',
                    border: `2px solid ${alpha(theme.palette.common.white, 0.2)}`,
                    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
                    animation: 'pulse 3s ease-in-out infinite',
                    '&:hover': {
                      transform: 'scale(1.1) rotate(10deg)',
                      transition: 'transform 0.3s ease',
                    },
                    '@keyframes pulse': {
                      '0%, 100%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.05)' },
                    },
                  }}
                >
                  <School sx={{ fontSize: 48, color: 'white' }} />
                </Box>
              </Slide>
              <Slide direction="up" in={mounted} timeout={1000}>
                <Box>
                  <Typography 
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      mb: 1,
                      background: 'linear-gradient(135deg, white 0%, rgba(255,255,255,0.8) 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      letterSpacing: '-0.02em',
                      fontSize: '2.5rem',
                    }}
                  >
                    AttendEase
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, fontSize: '1.1rem' }}>
                    Smart Attendance Management System
                  </Typography>
                  
                  {/* Security Features */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                    {securityFeatures.map((feature, index) => (
                      <Fade in={mounted} timeout={1200 + index * 200} key={index}>
                        <Chip
                          icon={feature.icon}
                          label={feature.text}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.common.white, 0.15),
                            color: 'white',
                            border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                            backdropFilter: 'blur(10px)',
                            fontSize: '0.75rem',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.common.white, 0.25),
                              transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        />
                      </Fade>
                    ))}
                  </Box>
                </Box>
              </Slide>
            </Box>
          </Box>

          <CardContent sx={{ p: 5 }}>
            <Slide direction="up" in={mounted} timeout={1200}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" sx={{ mb: 2, fontWeight: 700, fontSize: '2rem' }}>
                  Welcome Back! 👋
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                  Sign in to your account to continue managing attendance
                </Typography>
              </Box>
            </Slide>

            {error && (
              <Slide direction="down" in timeout={600}>
                <Alert 
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                    '& .MuiAlert-icon': {
                      fontSize: 24,
                    },
                  }}
                >
                  {error}
                </Alert>
              </Slide>
            )}

            <Fade in={mounted} timeout={1400}>
              <Box 
                component="form" 
                onSubmit={handleSubmit} 
                noValidate
                onFocus={() => setFormFocused(true)}
                onBlur={() => setFormFocused(false)}
              >
                <TextField
                  fullWidth
                  label="Username"
                  value={credentials.username}
                  onChange={handleInputChange('username')}
                  error={!!validationErrors.username}
                  helperText={validationErrors.username}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                          borderWidth: 2,
                        },
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px -8px ${alpha(theme.palette.primary.main, 0.3)}`,
                      },
                      '&.Mui-focused': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px -8px ${alpha(theme.palette.primary.main, 0.4)}`,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      fontWeight: 600,
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={handleInputChange('password')}
                  error={!!validationErrors.password}
                  helperText={validationErrors.password}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              transform: 'scale(1.1)',
                            },
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                          borderWidth: 2,
                        },
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px -8px ${alpha(theme.palette.primary.main, 0.3)}`,
                      },
                      '&.Mui-focused': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px -8px ${alpha(theme.palette.primary.main, 0.4)}`,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      fontWeight: 600,
                    },
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={credentials.rememberMe}
                        onChange={handleInputChange('rememberMe')}
                        color="primary"
                        sx={{
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            transform: 'scale(1.1)',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Remember me
                      </Typography>
                    }
                  />
                  <Link
                    href="#"
                    variant="body2"
                    color="primary"
                    sx={{
                      fontWeight: 500,
                      textDecoration: 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        textDecoration: 'underline',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    Forgot password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 2.5,
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    background: theme.palette.mode === 'light'
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'linear-gradient(135deg, #8360c3 0%, #2ebf91 100%)',
                    boxShadow: '0 8px 24px -8px rgba(102, 126, 234, 0.4)',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      transition: 'left 0.5s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 16px 40px -8px rgba(102, 126, 234, 0.6)',
                      background: theme.palette.mode === 'light'
                        ? 'linear-gradient(135deg, #5a67d8 0%, #667eea 100%)'
                        : 'linear-gradient(135deg, #7c3aed 0%, #8360c3 100%)',
                      '&::before': {
                        left: '100%',
                      },
                    },
                    '&:active': {
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.primary.main, 0.6),
                      color: 'white',
                      transform: 'none',
                    },
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                      Signing in...
                    </Box>
                  ) : (
                    'Sign In to Dashboard'
                  )}
                </Button>
              </Box>
            </Fade>

            {/* Enhanced Footer */}
            <Fade in={mounted} timeout={1600}>
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 3 }}>
                  <Chip 
                    icon={<Security style={{ fontSize: 16 }} />}
                    label="Secure Login" 
                    size="small" 
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                      fontWeight: 600,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    }} 
                  />
                </Divider>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    textAlign: 'center',
                    color: 'text.secondary',
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                  }}
                >
                  Protected by enterprise-grade security measures.<br />
                  Your data is encrypted and secure with role-based access control.
                </Typography>
              </Box>
            </Fade>
          </CardContent>
        </Card>
      </Fade>
    </Box>
  );
} 